const carouselArrow = (label, prev = false) => `
<button disabled class="card-carousel__arrow card-carousel__arrow--${prev ? 'prev' : 'next' }" aria-label="${label}" ${prev ? 'style="transform: scaleX(-1)"' : ''}>
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M26.1201 43.76L37.8801 32L26.1201 20.24" stroke="#212121" stroke-width="1.28"/>
    </svg>
</button>`;

const indicator = `
<div class="card-carousel_indicator"></div>
`

const indicators = (count) => `
    ${[...Array(count).keys()].map(_ => indicator).join('')}
`

class CarouselArrow extends HTMLElement {
    static get observedAttributes() {
        return ['direction', 'target'];
    }

    constructor() {
        super();
        this.role = 'button';
        this.setAttribute('disabled', '')

        // Nosto's Frequently Bought Together template ships `next="next"` rather
        // than `direction="next"`. Without a direction the arrow never gets its
        // left/right CSS, never re-disables itself at the end of the track, and
        // scrolls backwards when clicked — so normalise it here (WEB-3155).
        // The real fix belongs in the Nosto template; this keeps a typo from
        // producing a stuck, backwards arrow.
        //
        // applyDirection() rather than setAttribute() alone: attributes set from
        // inside the constructor don't fire attributeChangedCallback, so the
        // element would get its CSS but never its behaviour.
        if (!this.hasAttribute('direction')) {
            if (this.hasAttribute('prev')) this.applyDirection('prev');
            else if (this.hasAttribute('next')) this.applyDirection('next');
        }

        const small = this.getAttribute('size') === 'small';

        this.innerHTML = small ? `
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0.5 8C0.5 3.85786 3.85786 0.5 8 0.5C12.1421 0.5 15.5 3.85786 15.5 8C15.5 12.1421 12.1421 15.5 8 15.5C3.85786 15.5 0.5 12.1421 0.5 8Z" fill="white" fill-opacity="0.8"/>
                <path d="M0.5 8C0.5 3.85786 3.85786 0.5 8 0.5C12.1421 0.5 15.5 3.85786 15.5 8C15.5 12.1421 12.1421 15.5 8 15.5C3.85786 15.5 0.5 12.1421 0.5 8Z" stroke="#E1E1E1"/>
                <path d="M6.59998 10.8L9.39998 7.99999L6.59998 5.19999" stroke="#212121" stroke-width="0.5"/>
            </svg>        
        `: `
            <svg width="40" height="40" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M26.1201 43.76L37.8801 32L26.1201 20.24" stroke="#212121" stroke-width="1.28"/>
            </svg>
        `;

        this.addEventListener('click', () => {
            if (this.target) {
                this.target.style.scrollSnapType = '';
                const scrollAmount = this.target.offsetWidth * 0.8;
                this.target.scrollBy({left: this.direction === 'next' ? scrollAmount : -scrollAmount, behavior: 'smooth'});
            }
        });

        this.onScrollHandler = () => {
            // No direction and none inferable — stay hidden rather than sit on
            // the page as an arrow that scrolls the wrong way (WEB-3155).
            if (!this.direction || !this.target) this.setAttribute('disabled', '');
            else if (this.direction == 'prev' && this.target.scrollLeft < 4) this.setAttribute('disabled', '');
            else if (this.direction == 'next' && this.target.scrollLeft + this.target.offsetWidth > this.target.scrollWidth - 4) this.setAttribute('disabled', '');
            else this.removeAttribute('disabled');
        }

        this.parentElement.addEventListener('mouseleave', () => this.classList.remove('carousel-arrow--hover'));
        this.parentElement.addEventListener('mousemove', e => {
            const { left, width } = this.getBoundingClientRect();
            const midline = left + width / 2;
            this.classList.toggle('carousel-arrow--hover', Math.abs(e.clientX - midline) < 100);
        }, { passive: true });

        window.addEventListener('resize', () => this.onScrollHandler());

        const observer = new IntersectionObserver(entries => {
            if (entries.some(e => e.isIntersecting)) {
                this.onScrollHandler();
                observer.disconnect();
            }
        });
	    observer.observe(this);
    }

    attachTarget() {
        if (this.target) this.target.removeEventListener('scroll', this.onScrollHandler);
        this.target = this.parentElement.querySelector(this.targetSelector);

        const observer = new MutationObserver(() => {
            if (this.target && !this.target.isConnected) {
                observer.disconnect();
                this.attachTarget();
            }
        });

        observer.observe(this.parentElement, { childList: true, subtree: true });

        if (this.target) this.target.addEventListener('scroll', this.onScrollHandler);
        this.onScrollHandler();
    }

    /* Point the arrow in `value`, reflecting it to the attribute (for CSS) and
       to the element (for behaviour and labelling). */
    applyDirection(value) {
        if (this.getAttribute('direction') !== value) this.setAttribute('direction', value);
        this.style.transform = value === 'prev' ? 'scaleX(-1)' : '';
        this.direction = value;
        this.setAttribute('aria-label', value == 'next' ? 'Next' : 'Previous');
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'direction' && newValue && newValue.length > 0) {
            this.applyDirection(newValue);
        }

        if (name === 'target') {
            this.targetSelector = newValue;
            this.attachTarget();
        }
    }
}

customElements.define('carousel-arrow', CarouselArrow);

class CarouselItems extends HTMLElement {
    constructor() {
        super();

        // Mouse dragging logic for desktop
        this.querySelectorAll('img').forEach(i => i.draggable = false);
        this.querySelectorAll('a').forEach(i => i.draggable = false);

        let dragging = false;
        let pointerDisabled = false;
        let scrollStart = 0;
        let mouseStart = 0;

        if (this.dataset.drag !== "false") {
            this.addEventListener('mousedown', e => {
                if (window.matchMedia("(hover: hover)").matches) {
                    dragging = true;   
                    scrollStart = this.scrollLeft;
                    mouseStart = e.clientX;
                }
            });

            window.addEventListener('mousemove', e => {
                if (dragging) {
                    this.style.scrollSnapType = 'none';
                    this.scrollLeft = scrollStart - (e.clientX - mouseStart);
                    if (!pointerDisabled && Math.abs(this.scrollLeft - scrollStart) > 5) {
                        this.querySelectorAll(':scope > *').forEach(child =>  child.style.pointerEvents = 'none');
                        pointerDisabled = true;
                    }
                }
            })
        }

        window.addEventListener('click', () => {
            if (dragging) return false;
        })

        window.addEventListener('mouseup', e => {
            if (dragging) {
                dragging = false;
                scrollStart = 0;
                mouseStart = 0;
                pointerDisabled = false;
                this.querySelectorAll(':scope > *').forEach(child => child.style.pointerEvents = '');
                return false;
            }
        });
    }
}

customElements.define('carousel-items', CarouselItems);

class CardCarousel extends HTMLElement {
    constructor() {
        super();
        this.infinite = this.dataset.infinite === "true";

        const observer = new IntersectionObserver(entries => {
            if (entries.some(e => e.isIntersecting)) {
                this.updateButtons();
                observer.disconnect();
            }
        });
	    observer.observe(this);
    }

    connectedCallback() {
        this.insertAdjacentHTML('beforeend', carouselArrow('Next'));
        this.insertAdjacentHTML('beforeend', carouselArrow('Previous', true));

        this.items = this.querySelector('card-carousel-items');
        this.items.role = 'list';
        this.nextButton = this.querySelector('.card-carousel__arrow--next');
        this.prevButton = this.querySelector('.card-carousel__arrow--prev');

        this.indicatorsContainer = document.createElement('div');
        this.indicatorsContainer.className = 'card-carousel_indicators';
        this.appendChild(this.indicatorsContainer);        


        this.nextButton.addEventListener('click', () => {
            let items = this.items;
            const itemsStyle = getComputedStyle(items);
            let scrollAmount = items.offsetWidth - (parseInt(itemsStyle.paddingLeft) || 0) * 2 + (parseInt(itemsStyle.gap) || 0);

            items.style.scrollSnapType = '';
            items.scrollBy({
                left: scrollAmount,
                behavior: 'smooth'
            });

            this.stopAutoplay();
        });

        this.prevButton.addEventListener('click', () => {
            let items = this.items;
            const itemsStyle = getComputedStyle(items);
            let scrollAmount = items.offsetWidth - (parseInt(itemsStyle.paddingLeft) || 0) * 2 + (parseInt(itemsStyle.gap) || 0);

            items.style.scrollSnapType = '';
            items.scrollBy({
                left: -scrollAmount,
                behavior: 'smooth'
            });

            this.stopAutoplay();
        });

        let scrollHandle = null;

        this.items.addEventListener('scroll', e => {
            this.setAttribute('interacted', '');

            let items = this.items;
            this.updateButtons();
            this.updateIndicators();

            if (this.infinite) {
                if (items.scrollLeft == 0) {
                    items.scrollLeft = items.scrollWidth - items.clientWidth - items.lastElementChild.offsetWidth;
                    this.classList.add('fade-end');
                    setTimeout(() => this.classList.remove('fade-end'), 300);
                }
                else if (items.scrollLeft >= items.scrollWidth - items.clientWidth - 10) {
                    items.scrollLeft = 0 + items.firstElementChild.offsetWidth;
                    this.classList.add('fade-start');
                    setTimeout(() => this.classList.remove('fade-start'), 300);
                }
            }

            let card = Math.round(items.scrollLeft / (items.scrollWidth - items.lastElementChild.offsetWidth) * this.itemCount);
            if (this.infinite) card = card - 1;
            if (card != this.card) {
                this.card = card;
                items.querySelectorAll(':scope > *:not(.card-carousel__infinite-item').forEach((item, idx) => item.dataset.activeItem = idx == this.card ? "true" : "false");
                this.dataset.card = this.card + 1;
                if (!this.autoplayHandler) this.dispatchEvent(new CustomEvent('cardchange', { detail: { index: this.card } }));
            }

            if (!scrollHandle) {
                this.dataset.scrolling = "true";
                scrollHandle = setTimeout(() => {
                    this.dataset.scrolling = "false";
                    scrollHandle = null;

                    if (this.infinite) {    
                        if (items.scrollLeft == 0) {
                            items.scrollLeft = items.scrollWidth - items.clientWidth - items.lastElementChild.offsetWidth;
                            this.classList.add('fade-end');
                            setTimeout(() => this.classList.remove('fade-end'), 300);
                        }
                        else if (items.scrollLeft >= items.scrollWidth - items.clientWidth - 10) {
                            items.scrollLeft = 0 + items.firstElementChild.offsetWidth;
                            this.classList.add('fade-start');
                            setTimeout(() => this.classList.remove('fade-start'), 300);
                        }
                    }
                }, 300);
            }
        }, { passive: this.infinite ? false : true });

        let prevScrollLeft = this.items.scrollLeft;

        this.items.addEventListener('touchstart', e => {
            prevScrollLeft = this.items.scrollLeft;
        })

        this.items.addEventListener('touchmove', e => {
            if (e.isTrusted && Math.abs(this.items.scrollLeft - prevScrollLeft > 2)) {
                this.stopAutoplay();
                prevScrollLeft = this.items.scrollLeft;
            }
        }, { passive: true })

        window.addEventListener('resize', () => {
            this.updateButtons();
            this.updateIndicators();
        });

        // Mouse dragging logic for desktop
        this.items.querySelectorAll('img').forEach(i => i.draggable = false);
        this.items.querySelectorAll('a').forEach(i => i.draggable = false);

        let dragging = false;
        let pointerDisabled = false;
        let scrollStart = 0;
        let mouseStart = 0;

        if (this.dataset.drag !== "false") {
            this.items.addEventListener('mousedown', e => {
                if (window.matchMedia("(hover: hover)").matches) {
                    dragging = true;   
                    scrollStart = this.items.scrollLeft;
                    mouseStart = e.clientX;
                }
            });

            window.addEventListener('mousemove', e => {
                if (dragging) {
                    this.items.style.scrollSnapType = 'none';
                    this.items.scrollLeft = scrollStart - (e.clientX - mouseStart);
                    if (!pointerDisabled  && Math.abs(this.items.scrollLeft - scrollStart) > 5) {
                        this.items.querySelectorAll(':scope > *').forEach(child =>  child.style.pointerEvents = 'none');
                        pointerDisabled = true;
                    }
                }
            })
        }

        this.addEventListener('mousemove', e => {
            const { left, right } = this.getBoundingClientRect();
            this.classList.toggle('card-carousel--hover-left', e.clientX - left < 100);
            this.classList.toggle('card-carousel--hover-right', right - e.clientX < 100);
        }, { passive: true });

        this.addEventListener('mouseleave', e => this.classList.remove('card-carousel--hover-left', 'card-carousel--hover-right'));

        window.addEventListener('click', () => {
            if (dragging) return false;
        })

        window.addEventListener('mouseup', e => {
            if (dragging) {
                dragging = false;
                scrollStart = 0;
                mouseStart = 0;
                pointerDisabled = false;
                this.items.querySelectorAll(':scope > *').forEach(child => child.style.pointerEvents = '');
                return false;
            }
        });

        this.update();
        this.resetScroll();

        this.classList.add('init');
    }

    update() {
        // Remove old elements
        this.querySelectorAll('.card-carousel__infinite-item').forEach(item => item.remove());

        this.card = 0;
        this.dataset.card = 1;

        if (this.infinite && this.items.children.length > 0) {
            let scrollLeft = this.items.scrollLeft;
            let first = this.items.children[0].cloneNode(true);
            let last = this.items.children[this.items.children.length - 1].cloneNode(true);
            first.classList.add('card-carousel__infinite-item');
            last.classList.add('card-carousel__infinite-item');
            this.items.insertAdjacentElement('afterbegin', last);
            this.items.insertAdjacentElement('beforeend', first);
            this.items.scrollLeft = scrollLeft + first.offsetWidth;
        }

        this.itemCount = this.items.querySelectorAll(':scope > *').length;
        this.indicatorsContainer.innerHTML = indicators(this.itemCount);
        this.indicatorElems = this.indicatorsContainer.querySelectorAll('.card-carousel_indicator');

        for (let [idx, indicator] of this.indicatorElems.entries()) {
            indicator.addEventListener('click', e => {
                this.scrollToIndex(idx);
                this.stopAutoplay();
            });
        }

        setTimeout(() => {
            this.updateIndicators();
            this.updateButtons();
        });
    }

    resetScroll() {
        for (let child of this.items.children) child.dataset.activeItem = "false";

        if (this.infinite && this.items.children.length > 0) {
            this.items.scrollLeft = this.items.children[0].offsetWidth;
            this.items.children[1].dataset.activeItem = "true";
        }
        else {
            this.items.scrollLeft = 0;
            if (this.items.children.length > 0) this.items.children[0].dataset.activeItem = "true";
        }
    }

    updateIndicators() {
        if (!this.indicatorElems) return;

        let items = this.items;
        let cards = parseFloat(getComputedStyle(items).getPropertyValue('--num-cards'));
        let indicatorSpan = items.scrollWidth / this.itemCount;
        let targetIdx = Math.floor(items.scrollLeft / indicatorSpan + 0.3);
        let limit = this.itemCount - (Math.floor(cards) - 1);
        if (items.scrollWidth - items.offsetWidth - items.scrollLeft < 50) targetIdx = limit - 1;
        this.indicatorElems.forEach((elem, idx) => {
            if (idx == targetIdx) elem.classList.add('filled');
            else elem.classList.remove('filled');

            if (idx >= limit) elem.classList.add('hidden');
            else (elem.classList.remove('hidden'));
        });
    }

    updateButtons() {
        if (this.items.scrollLeft < 20) this.prevButton.disabled = true;
        else this.prevButton.disabled = false;

        if (this.items.scrollLeft > (this.items.scrollWidth - this.items.offsetWidth - 20)) this.nextButton.disabled = true;
        else this.nextButton.disabled = false;
    }

    scrollToIndex(index, smooth = true, fade = false) {
        if (this.infinite) index = index + 1;
        let items = this.querySelector('card-carousel-items');
        let itemCount = this.querySelectorAll('card-carousel-items > *').length;
        let itemWidth = items.scrollWidth / itemCount;
        items.scrollTo({left: itemWidth * index, behavior: smooth ? 'smooth' : 'auto'});
    }

    autoplay(interval, fade = false) {
        let item = 0;
        let itemsElem = this.querySelector('card-carousel-items');
        let items =  [...this.querySelectorAll('card-carousel-items > *')];
        let numItems = items.length;

        if (fade) itemsElem.style.scrollSnapType = 'none';

        this.autoplayHandler = setInterval(() => {
            let currentItem = items[item];

            if (fade) currentItem.classList.add('transition-out');

            item++;
            if (item >= numItems) {
                item = 0;
                
                if (!fade) {
                    this.classList.add('scrollback');
                    setTimeout(() => this.classList.remove('scrollback'), 1000);
                }
            }
            this.scrollToIndex(item, !fade);

            if (fade) {
                currentItem.style.transform = `translateX(${itemsElem.scrollLeft - currentItem.offsetLeft}px)`;
                setTimeout(() => {
                    currentItem.style.transform = '';
                    currentItem.classList.remove('transition-out');
                }, 500);
            }
        }, interval);
    }

    stopAutoplay() {
        if (this.autoplayHandler)  {
            clearInterval(this.autoplayHandler);
            this.autoplayHandler = undefined;
            this.querySelector('card-carousel-items').style.scrollSnapType = '';
        }
    }
}

if (!customElements.get('card-carousel')) customElements.define('card-carousel', CardCarousel);


class ImageCarouselTab extends HTMLElement {
    constructor() {
        super();

        this.addEventListener('click', () => {
            const parent = this.closest('.image-carousel');
            if (parent) {
                parent.setAttribute('data-tab', this.dataset.tab);
                const carousel = parent.querySelector('card-carousel');
                if (carousel) setTimeout(() => {
                    carousel.update();
                    carousel.resetScroll();
                });
            }
        });
    }
}

customElements.define('image-carousel-tab', ImageCarouselTab);